<template>
    <v-container fluid>
        <v-row np-gutters>
            <v-col cols="12" class="mx-0 px-0">
                <template>
                    <v-data-table
                        :headers="headers"
                        :items="players.filter((player) => player.active)"
                        class="stats-table no-global-table-hover elevation-1 rounded-lg"
                        hide-default-footer
                        :mobile-breakpoint="0"
                        :items-per-page="-1"
                        @click:row="goToPlayer"
                    >
                        <template v-slot:header.ts_percentage="{ header }">
                            <div class="d-inline-flex align-center">
                                <span>{{ header.text }}</span>
                                <info-tip bottom max-width="380" content-class="ts-tooltip-surface">
                                    <v-icon
                                        small
                                        class="ml-1 ts-info-icon"
                                        color="orange"
                                    >
                                        mdi-information-outline
                                    </v-icon>
                                    <template #content>
                                        <div class="ts-tooltip">
                                            <div class="font-weight-bold mb-1">True Shooting Percentage (TS%)</div>
                                            <div class="mb-2">TS% = Points / (2 x (Field Goal Attempts + 0.44 x Free Throw Attempts))</div>
                                            <div class="ts-poor">Below 50%: Poor scoring efficiency</div>
                                            <div class="ts-below-average">50% to below 55%: Below average</div>
                                            <div class="ts-average">55%: Solid and league average</div>
                                            <div class="ts-very-good">57% to 58%: Very good / highly efficient</div>
                                            <div class="ts-elite">60% and above: Elite</div>
                                        </div>
                                    </template>
                                </info-tip>
                            </div>
                        </template>
                        <template v-slot:item.player="{ item }">
                            <div class="entity-cell">
                                <span class="entity-cell__thumb">
                                    <img :src="item.image ? `${assetsUrl}/${item.image}` : defaultPlayerImg" alt="" @error="$event.target.src = defaultPlayerImg">
                                </span>
                                <span class="entity-cell__name">{{ item.surname }} {{ item.name }}</span>
                            </div>
                        </template>
                        <template v-slot:item.team="{ item }">
                            <div class="entity-cell">
                                <span v-if="item.teamLogo" class="entity-cell__thumb">
                                    <img :src="`${assetsUrl}/${item.teamLogo}`" alt="">
                                </span>
                                <span class="entity-cell__name">{{ item.team }}</span>
                            </div>
                        </template>
                        <template v-slot:item.two_points_percentage="{ item }">
                            <span class="font-weight-bold"> {{ option ? `${item.two_points_percentage}%
                                (${item.cum.twoPointsMade} / ${item.cum.twoPointAttempts})` :
                                `${item.two_points_percentage}%` }} </span>
                        </template>
                        <template v-slot:item.three_points_percentage="{ item }">
                            <span class="font-weight-bold"> {{ option ? `${item.three_points_percentage}%
                                (${item.cum.threePointsMade} / ${item.cum.threePointAttempts})` :
                                `${item.three_points_percentage}%` }} </span>
                        </template>
                        <template v-slot:item.free_throw_percentage="{ item }">
                            <span class="font-weight-bold"> {{ option ? `${item.free_throw_percentage}%
                                (${item.cum.freeThrowsMade} / ${item.cum.freeThrowAttempts})` :
                                `${item.free_throw_percentage}%` }} </span>
                        </template>
                        <template v-slot:item.ts_percentage="{ item }">
                            <span :class="['font-weight-bold', tsClass(item.ts_percentage)]">
                                {{ Number(item.ts_percentage).toFixed(1) }}%
                            </span>
                        </template>
                        <template v-slot:item.market="{ item }">
                            <span class="custom-orange-text market-value-text" style="font-size: 15px;"> {{ item.averagePir.toFixed(2) }}
                            </span>
                            <span v-if="item.pirDates">
                                <v-icon
                                    v-if="item.averagePir > (item.pirDates.slice(0, -1).reduce((a, b) => a + b.pir, 0) / item.pirDates.slice(0, -1).length)"
                                    color="green"
                                > mdi-chevron-double-up </v-icon>
                                <v-icon
                                    v-else-if="item.averagePir < (item.pirDates.slice(0, -1).reduce((a, b) => a + b.pir, 0) / item.pirDates.slice(0, -1).length)"
                                    color="red"
                                > mdi-chevron-double-down </v-icon>
                                <v-icon
                                    v-else-if="item.averagePir === (item.pirDates.slice(0, -1).reduce((a, b) => a + b.pir, 0) / item.pirDates.slice(0, -1).length)"
                                    color="grey"
                                > mdi-arrow-left-right-bold </v-icon>
                            </span>
                            <v-sparkline
                                v-if="item.pirDates"
                                :value="item.pirDates ? item.pirDates.map(element => element.pir) : [0]"
                                :gradient="sparkline.gradient"
                                :smooth="sparkline.radius || false"
                                :padding="sparkline.padding"
                                :line-width="sparkline.width"
                                :stroke-linecap="sparkline.lineCap"
                                :gradient-direction="sparkline.gradientDirection"
                                :fill="sparkline.fill"
                                :type="sparkline.type"
                                :auto-line-width="sparkline.autoLineWidth"
                                height="50"
                                auto-draw
                                color="black"
                            />
                        </template>
                        <template v-slot:item.playTimeInSecs="{ item }">
                            <span>
                                {{ option === 0 ? secToMinutesSeconds(item.avg.playTimeInSecs) :
                                    secToMinutesSeconds(item.cum.playTimeInSecs) }}
                            </span>
                        </template>
                    </v-data-table>
                </template>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    const gradients = [
        ['#ffd200'],
        ['#ffd200'],
        ['red', 'orange', 'yellow'],
        ['purple', 'violet'],
        ['#ffd200', '#F0F', '#FF0'],
        ['#f72047', '#ffd200', '#ffd200']
    ]

    export default {
        props: {
            players: {
                type: Array,
                default: () => []
            },
            option: {
                type: Number,
                default: 0
            },
            generic: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                headers: [
                    { text: 'Player', value: 'player', align: 'start' },
                    {
                        text: '# Number',
                        value: 'jersey',
                        align: 'center'
                    },
                    { text: 'Market value', value: 'market' },
                    { text: 'GP', value: 'gp', align: 'start' },
                    { text: 'Pts', value: 'points' },
                    { text: '2P%', value: 'two_points_percentage', width: '150px', align: 'center' },
                    { text: '3P%', value: 'three_points_percentage', width: '150px', align: 'center' },
                    { text: 'FT%', value: 'free_throw_percentage', width: '150px', align: 'center' },
                    { text: 'TS%', value: 'ts_percentage', width: '100px', align: 'center' },
                    { text: 'OR', value: 'offensiveRebounds', align: 'start' },
                    { text: 'DR', value: 'defensiveRebounds', align: 'start' },
                    { text: 'TR', value: 'rebounds', align: 'start' },
                    { text: 'AST', value: 'assists', align: 'start' },
                    { text: 'AST PTS', value: 'assistPoints', align: 'start' },
                    { text: '2CH PTS', value: 'secondChancePoints', align: 'start' },
                    { text: 'PTS OFF TO', value: 'pointsOffTurnovers', align: 'start' },
                    { text: 'STL', value: 'steals', align: 'start' },
                    { text: 'TO', value: 'turnovers', align: 'start' },
                    { text: 'BLK', value: 'blockedShots', align: 'start' },
                    { text: 'FC', value: 'fouls', align: 'start' },
                    { text: 'PIR', value: 'pir', align: 'center' },
                    { text: 'Min', value: 'playTimeInSecs', align: 'center', width: '100px' }
                ],
                maxGames: 1,
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                sparkline: {
                    width: 2,
                    radius: 10,
                    padding: 8,
                    lineCap: 'round',
                    gradient: gradients[5],
                    gradientDirection: 'top',
                    gradients,
                    fill: true,
                    type: 'trend',
                    autoLineWidth: false
                }
            }
        },
        watch: {
            generic: {
                handler() {
                    if (this.generic) {
                        this.headers.splice(1, 0, { text: 'Team', value: 'team' })
                    }
                }
            },
            option: {
                handler() {
                    this.prepareData()
                },
                immediate: true
            },
            players: {
                handler() {
                    this.prepareData()
                },
                immediate: true,
                deep: true
            }
        },
        created() {
            if (this.generic) {
                this.headers.splice(1, 0, { text: 'Team', value: 'team' })
            }
            this.prepareData()
        },
        methods: {
            getPlayerId(player) {
                return player.id || player.playerId || (player.player ? player.player.id : null)
            },
            goToPlayer(player) {
                const playerId = this.getPlayerId(player)
                if (!playerId) {
                    return
                }
                this.$router.push({ name: 'Player', params: { id: playerId } })
            },
            secToMinutesSeconds(ms) {
                if (ms < 0) return '-'
                if (typeof ms !== 'number' || isNaN(ms)) return '-'
                const minutes = Math.floor(ms / 60)
                const seconds = Math.floor(ms % 60)
                if (isNaN(minutes) || isNaN(seconds)) return '-'
                return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
            },
            toFixedSafe(v, digits = 1) {
                const n = Number(v)
                return Number.isFinite(n) ? n.toFixed(digits) : '0.0'
            },
            pctSafe(made, attempts, digits = 1) {
                const m = Number(made) || 0
                const a = Number(attempts) || 0
                if (a === 0) return (0).toFixed(digits)
                return ((m / a) * 100).toFixed(digits)
            },
            tsPctSafe(points, twoPointAttempts, threePointAttempts, freeThrowAttempts, digits = 1) {
                const pts = Number(points) || 0
                const twoPa = Number(twoPointAttempts) || 0
                const threePa = Number(threePointAttempts) || 0
                const fta = Number(freeThrowAttempts) || 0
                const denominator = 2 * (twoPa + threePa + (0.44 * fta))
                if (denominator === 0) return (0).toFixed(digits)
                return ((pts / denominator) * 100).toFixed(digits)
            },
            numOrZero(v) {
                const n = Number(v)
                return Number.isFinite(n) ? n : 0
            },
            tsClass(tsValue) {
                const ts = Number(tsValue)
                if (!Number.isFinite(ts)) return 'ts-poor'
                if (ts < 50) return 'ts-poor'
                if (ts < 55) return 'ts-below-average'
                if (ts < 57) return 'ts-average'
                if (ts < 60) return 'ts-very-good'
                return 'ts-elite'
            },
            prepareData() {
                if (!Array.isArray(this.players)) return

                this.players.forEach(player => {
                    const avg = player?.avg || {}
                    const cum = player?.cum || {}
                    const statsLen = Array.isArray(player?.stats) ? player.stats.length : 0
                    if (statsLen > this.maxGames) this.maxGames = statsLen

                    // Ensure fields exist to prevent template errors
                    player.player = `${player.name || ''} ${player.surname || ''}`.trim()
                    player.gp = statsLen

                    // Points and common stats
                    player.points = this.option ? this.numOrZero(cum.points) : this.toFixedSafe(avg.points, 1)
                    player.offensiveRebounds = this.option ? this.numOrZero(cum.offensiveRebounds) : this.toFixedSafe(avg.offensiveRebounds, 1)
                    player.defensiveRebounds = this.option ? this.numOrZero(cum.defensiveRebounds) : this.toFixedSafe(avg.defensiveRebounds, 1)
                    player.rebounds = this.option ? this.numOrZero(cum.rebounds) : this.toFixedSafe(avg.rebounds, 1)
                    player.assists = this.option ? this.numOrZero(cum.assists) : this.toFixedSafe(avg.assists, 1)
                    // points of the baskets the player assisted
                    player.assistPoints = this.option ? this.numOrZero(cum.assistPoints) : this.toFixedSafe(avg.assistPoints, 1)
                    // points scored after an offensive rebound of the team
                    player.secondChancePoints = this.option ? this.numOrZero(cum.secondChancePoints) : this.toFixedSafe(avg.secondChancePoints, 1)
                    // points scored after a turnover of the opponent
                    player.pointsOffTurnovers = this.option ? this.numOrZero(cum.pointsOffTurnovers) : this.toFixedSafe(avg.pointsOffTurnovers, 1)
                    player.steals = this.option ? this.numOrZero(cum.steals) : this.toFixedSafe(avg.steals, 1)
                    player.turnovers = this.option ? this.numOrZero(cum.turnovers) : this.toFixedSafe(avg.turnovers, 1)
                    player.blockedShots = this.option ? this.numOrZero(cum.blockedShots) : this.toFixedSafe(avg.blockedShots, 1)
                    // every foul: personal, technical and unsportsmanlike
                    player.fouls = this.option ? this.numOrZero(cum.fouls) : this.toFixedSafe(avg.fouls, 1)

                    // Percentages
                    player.two_points_percentage = this.option
                        ? this.pctSafe(cum.twoPointsMade, cum.twoPointAttempts, 1)
                        : this.pctSafe(avg.twoPointsMade, avg.twoPointAttempts, 1)

                    player.free_throw_percentage = this.option
                        ? this.pctSafe(cum.freeThrowsMade, cum.freeThrowAttempts, 1)
                        : this.pctSafe(avg.freeThrowsMade, avg.freeThrowAttempts, 1)

                    player.three_points_percentage = this.option
                        ? this.pctSafe(cum.threePointsMade, cum.threePointAttempts, 1)
                        : this.pctSafe(avg.threePointsMade, avg.threePointAttempts, 1)

                    player.ts_percentage = this.option
                        ? this.tsPctSafe(cum.points, cum.twoPointAttempts, cum.threePointAttempts, cum.freeThrowAttempts, 1)
                        : this.tsPctSafe(avg.points, avg.twoPointAttempts, avg.threePointAttempts, avg.freeThrowAttempts, 1)

                    // PIR and play time
                    player.pir = this.option ? this.numOrZero(cum.pir) : this.toFixedSafe(avg.pir, 1)
                    // Ensure averagePir always exists to avoid template toFixed error
                    if (player.averagePir == null) player.averagePir = 0

                    // Normalize play time
                    player.playTimeInSecs = this.option
                        ? this.numOrZero(cum.playTimeInSecs)
                        : this.numOrZero(avg.playTimeInSecs)
                })
            }
        }
    }
</script>

<style scoped>
/* ---------- Same look as the standings table ---------- */
.stats-table {
    overflow: hidden;
    font-family: Ubuntu, Arial, sans-serif;
}

.stats-table ::v-deep .v-data-table__wrapper table > thead > tr > th {
    background: #0d0d0f !important;
    color: #fff !important;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    border-bottom: 3px solid #ff7133 !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > thead > tr > th .v-icon {
    color: #fff !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr > td {
    height: 52px;
    white-space: nowrap;
    background: #fff;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr:nth-child(even) > td {
    background: #f7f7f8 !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td {
    background: #fff3ec !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td:first-child {
    box-shadow: inset 4px 0 0 #ff7133;
}

.stats-table ::v-deep tbody tr {
    cursor: pointer;
}

.entity-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.entity-cell__thumb {
    display: inline-flex;
    flex: 0 0 auto;
    width: 34px;
    height: 34px;
    padding: 2px;
    border-radius: 6px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.entity-cell__thumb img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.entity-cell__name {
    font-weight: 700;
    white-space: normal;
    line-height: 1.2;
}

.ts-info-icon {
    cursor: pointer;
}

.ts-tooltip {
    font-size: 13px;
    line-height: 1.4;
}

::v-deep .v-tooltip__content.ts-tooltip-surface {
    background: #fff !important;
    opacity: 1 !important;
    color: #111;
    border: 1px solid #d7dbe3;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.ts-poor {
    color: #d32f2f;
}

.ts-below-average {
    color: #ef6c00;
}

.ts-average {
    color: #f9a825;
}

.ts-very-good {
    color: #2e7d32;
}

.ts-elite {
    color: #1b5e20;
}
</style>
